import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XCircle, CheckCircle, Calendar, DollarSign, User, Clock, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

export default function AdminCancellations() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/dashboard');
      return;
    }
    fetchCancellations();
  }, [user, navigate]);

  const fetchCancellations = async () => {
    try {
      const response = await apiClient.get('/admin/cancellations');
      setCancellations(response.data);
    } catch (error) {
      toast.error('Failed to load cancellation requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (cancellationId, bookingId) => {
    if (!window.confirm('Are you sure you want to approve this cancellation? This will cancel the booking.')) {
      return;
    }

    try {
      await apiClient.put(`/admin/cancellations/${cancellationId}/approve`);
      toast.success('Cancellation approved. Booking has been cancelled.');
      fetchCancellations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve cancellation');
    }
  };

  const handleReject = async (cancellationId) => {
    const reason = window.prompt('Please provide a reason for rejecting this cancellation:');
    if (!reason) return;

    try {
      await apiClient.put(`/admin/cancellations/${cancellationId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Cancellation request rejected.');
      fetchCancellations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject cancellation');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <XCircle className="text-red-400" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Cancellation Requests</h1>
            <p className="text-white/60">Review and process booking cancellations</p>
          </div>
        </div>

        {/* Cancellation Requests */}
        {cancellations.length === 0 ? (
          <Card className="glass-default border-white/10">
            <CardContent className="p-12 text-center">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
              <p className="text-xl text-white/60">No pending cancellation requests</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {cancellations.map((cancel) => (
              <Card key={cancel.cancellation_id} className="glass-default border-white/10">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="text-yellow-400" size={20} />
                        <span className="font-semibold">Cancellation Request</span>
                        <span className="text-xs text-white/40">
                          {new Date(cancel.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-white/60">
                          <Calendar size={16} />
                          <span>Event: {cancel.booking?.event_date ? new Date(cancel.booking.event_date).toLocaleDateString() : 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <DollarSign size={16} />
                          <span>Amount: ${cancel.booking?.total_cost?.toFixed(2) || '0.00'}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <User size={16} />
                          <span>Booking: {cancel.booking_id}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/60">
                          <Clock size={16} />
                          <span>Payment: {cancel.booking?.payment_status || 'N/A'}</span>
                        </div>
                      </div>

                      {cancel.reason && (
                        <div className="bg-white/5 rounded-lg p-3">
                          <p className="text-sm text-white/80">
                            <span className="text-white/40">Reason: </span>
                            {cancel.reason}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApprove(cancel.cancellation_id, cancel.booking_id)}
                        className="bg-green-600 hover:bg-green-500"
                      >
                        <CheckCircle size={16} className="mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleReject(cancel.cancellation_id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/20"
                      >
                        <XCircle size={16} className="mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
